import { toRequestAttribution } from "@p0security/shared/permission-requests/attribution";
import type { Actor } from "@p0security/shared/types/index";
import { subjectOf } from "components/Agentic/DelegationChain";

import type { RequestAttribution } from "../../Jit/Requests/types.ts";

/** Who the clientId's delegation chain was ultimately acting for, as a
 * `RequestAttribution` with role "principal" (the chain's own subject, not
 * a delegate or proxy of anything else). Undefined when `subjectOf` can't
 * determine a subject. */
export const attributionOf = (
  actors: Actor[] | undefined,
  caller: string | undefined
): RequestAttribution | undefined => {
  if (!actors || actors.length === 0) return undefined;
  const subject = subjectOf(actors, caller);
  if (!subject) return undefined;
  const lastActor = actors[actors.length - 1];
  const { requestor } = toRequestAttribution({
    agentic: {
      agents: actors,
      // Mirrors delegatesOf's own dedup condition (subjectOf only returns the
      // chain's own last actor, rather than a synthetic appended entry, when
      // that actor already is the subject) — not a new trust decision: both
      // sides of this comparison come from the same backend-validated token
      // chain, never independently attacker-influenced.
      headless: subject.sub === lastActor.sub,
      // Not read by toRequestAttribution; only agents/headless matter here.
      session: lastActor,
    },
    principal: subject.sub,
    requestor: subject.sub,
  });
  return { ...requestor, role: "principal" };
};
